import { BuiltInAttribute } from "@orsane/schema/tasks";
import { addWeeks, type Day, endOfWeek, format, isPast, isThisWeek, isToday, isTomorrow, startOfWeek } from "date-fns";
import { CalendarDays } from "lucide-react";
import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import DueDateSelect from "@/components/property-selectors/DueDateSelect";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { cn } from "@/lib/utils";
import { useUpdateTaskPropertiesMutation } from "@/mutations/task-mutations";
import type { ColumnDef } from "../ColumnModel";
import type { Row } from "../RowModel";
import { useBulkTargetIds } from "../useBulkTargetIds";

// Static function - `weekStartsOn` comes from the user's preferences
function getDueDateGroupingValue(dueDate: string | null, weekStartsOn: Day): string {
    if (!dueDate) return "1_No due date";

    const date = new Date(dueDate);

    if (isPast(date) && !isToday(date)) {
        return "0_Overdue";
    }

    if (isToday(date)) {
        return "2_Today";
    }

    if (isTomorrow(date)) {
        return "3_Tomorrow";
    }

    if (isThisWeek(date, { weekStartsOn })) {
        return "4_This week";
    }

    // Check if it's in the next week (7-14 days from now)
    const nextWeekStart = addWeeks(startOfWeek(new Date(), { weekStartsOn }), 1);
    const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn });

    if (date >= nextWeekStart && date <= nextWeekEnd) {
        return "5_Next week";
    }

    return "6_Later";
}

// Memoized cell component
const DueDateCell = memo(function DueDateCell({
    row,
    onDateChange,
}: {
    row: Row;
    onDateChange: (taskId: number, date: Date | null) => void;
}) {
    const { formatDate } = useDateFormatter();
    const handleChange = useCallback(
        (date: Date | null) => {
            onDateChange(row.data.id, date);
        },
        [row.data.id, onDateChange]
    );

    return (
        <DueDateSelect
            trigger={(value) => (
                <CellButton
                    disabled={row.data.isNewlyCreated}
                    className={cn("text-sm", {
                        "text-orsane-overdue": !!value && isPast(value),
                    })}
                >
                    {!!value && (
                        <>
                            <CalendarDays />
                            {formatDate(value)}
                        </>
                    )}
                </CellButton>
            )}
            value={row.data.dueDate}
            onChange={handleChange}
        />
    );
});

export function useDueDateColumnBuilder() {
    const { mutate } = useUpdateTaskPropertiesMutation();
    const getTargetIds = useBulkTargetIds();
    const { weekStartsOn } = useDateFormatter();

    // Stable mutation handler
    const handleDateChange = useCallback(
        (taskId: number, date: Date | null) => {
            const dueDate = date ? format(date, "yyyy-MM-dd") : null;
            for (const id of getTargetIds(taskId)) {
                mutate({
                    id,
                    updates: { dueDate },
                });
            }
        },
        [mutate, getTargetIds]
    );

    return {
        build: useCallback(
            (isVisible: boolean): ColumnDef => {
                return {
                    id: BuiltInAttribute.dueDate,
                    header: "Due",
                    isVisible: isVisible,
                    cssColDef: "166px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: ({ row }: { row: Row }) => getDueDateGroupingValue(row.data.dueDate, weekStartsOn),
                    getGroupHeader: ({ row }: { row: Row }) =>
                        getDueDateGroupingValue(row.data.dueDate, weekStartsOn).split("_")[1],
                    cell: ({ row }: { row: Row }) => <DueDateCell row={row} onDateChange={handleDateChange} />,
                };
            },
            [handleDateChange, weekStartsOn]
        ),
    };
}
